import { createSlice } from '@reduxjs/toolkit';
import { HOURS_IN_DAY, HOURS_IN_DAY_MINUS_ONE } from '../../constant/constants';

/**
 * Redux slice for managing date and time range filters.
 *
 * This slice handles the state related to date and time filters, including:
 * - `from`: The start date of the range.
 * - `to`: The end date of the range.
 * - `timeFromHour`: The starting hour of the time range.
 * - `timeToHour`: The ending hour of the time range.
 * - `timeFromMinute`: The starting minute of the time range (optional).
 * - `timeToMinute`: The ending minute of the time range (optional).
 *
 * It provides reducers to:
 * - Set the date and time range (`setRange`).
 * - Validate and adjust the time range to ensure logical consistency (`validateTimes`).
 *
 * Validates and adjusts the time range to ensure logical consistency.
 *
 * If the start and end dates are the same, but the end hour is less than the start hour,
 * the end hour is adjusted to be one hour after the start hour. This ensures that the time
 * range is always valid and avoids potential issues with invalid time intervals.
 *
 */

export interface DateTimeRange {
  from: string;
  to: string;
  timeFromHour: string;
  timeToHour: string;
  timeFromMinute?: string;
  timeToMinute?: string;
  searchResetCount: number;
}

const today = new Date();

const initialState: DateTimeRange = {
  from: today.toISOString().split('T')[0],
  to: today.toISOString().split('T')[0],
  timeFromHour: '00',
  timeToHour: '24',
  timeFromMinute: '00',
  timeToMinute: '00',
  searchResetCount: 0,
};

const dateFilterSlice = createSlice({
  name: 'dateFilter',
  initialState,
  reducers: {
    setRange(state, action) {
      return {
        ...state,
        ...action.payload,
      };
    },
    validateTimes(state) {
      if (!state.from || !state.to) return;

      const fromDate = new Date(state.from);
      const toDate = new Date(state.to);

      if (fromDate.toDateString() !== toDate.toDateString()) return;

      const fromHour = parseInt(state.timeFromHour || '0', 10);
      const toHour = parseInt(state.timeToHour || '24', 10);

      // Start hour cannot be 24 on the same day.
      if (fromHour >= HOURS_IN_DAY) {
        state.timeFromHour = HOURS_IN_DAY_MINUS_ONE.toString().padStart(2, '0');
        return;
      }

      // End hour must be greater than start hour.
      if (toHour <= fromHour) {
        const correctedTo = Math.min(fromHour + 1, HOURS_IN_DAY);

        state.timeToHour = correctedTo.toString().padStart(2, '0');
      }
    },
    resetRange() {
      return initialState;
    },
    resetSearch(state) {
      state.searchResetCount += 1;
    },
  },
});

export const { setRange, validateTimes, resetRange, resetSearch } = dateFilterSlice.actions;

export default dateFilterSlice.reducer;
